/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FSBTable } from 'components/common/FSBTable';
import { FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { FiEdit2 } from 'react-icons/fi';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import {
  MARKET_LIST_STATUS,
  offersColumns,
} from 'pages/MarketListing/constant';
import {
  OFFERS_QUERY,
  DELETE_OFFER,
  UPDATE_OFFER_STATUS,
} from 'pages/MarketListing/MarketListingGraphQL';
import StatusFormatter from 'components/StatusFormatter';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import Loader from 'components/common/Loader';
import { defaultDeleteFilter, showSequenceNumber } from 'utils/constants';
import {
  RECORD_DELETED_SUCCESSFULLY,
  STATUS_UPDATED_SUCCESSFULLY,
} from 'components/common/FSBToastify/types';
import { setBidDetails } from 'actions/bidDetailAction';
import { setVendorDetails } from 'actions/vendorDetailsAction';
import DownloadCSV from 'components/DownloadCSV';
import {
  EXPORTS_OFFERS,
} from 'utils/CommonGraphQL';
import { TYPE_OF_OFFER_AND_DEMAND } from 'helper/constants';
import { role } from 'config/role';
import RBAC from 'hoc/RBAC';
import ErrorMessage from 'components/ErrorMessage';
import NoDataFound from 'components/NoDataFound';
import FilterResultBar from 'components/FilterResultBar';
import MarketListingMobileView from 'pages/MarketListing/components/MarketListingMobileView';
import ReactivateListingModal from 'pages/MarketListing/components/ReactivateListingModal';

const renderRoleColumn = ({ row }) => (
  <div className="px-1 text-capitalize">
    {row?.original?.createdByUser?.traderType?.toLowerCase() || row?.original?.trader?.traderType?.toLowerCase() || '-'}
  </div>
);

const renderChildUserColumn = ({ row }) => {
  const offer = row?.original;
  const trader = offer?.trader;
  const createdByUser = offer?.createdByUser;

  // If createdByUser is different from trader, show createdByUser as child user
  if (createdByUser && trader && String(createdByUser.id) !== String(trader.id)) {
    const name = `${createdByUser.firstName || ''} ${createdByUser.lastName || ''}`.trim();
    const callingCode = createdByUser.callingCode?.callingCode || '';
    const phone = createdByUser.phone || '';
    const mobileNumber = phone ? `${callingCode}-${phone}` : '';
    return (
      <div>
        <div className="fw-medium">{name || '-'}</div>
        {mobileNumber && <div className="text-muted f-12">{mobileNumber}</div>}
      </div>
    );
  }

  return '-';
};

const LiveOffersTable = ({
  searchString,
  filterValue,
  handleResetFilter,
  showAdvancedSearchBar,
  selectedProductName,
}) => {
  const dispatch = useDispatch();
  const [deleteRowId, setDeleteRowId] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [showDeleteRecord, setDeleteRecord] = useState(false);
  const [filters, setFilters] = useState({
    filter: [
      {
        key: 'entityName',
        joinAttributeName: 'trader',
        value: searchString,
        type: 'STRING',
        op: 'LIKE_OR_NULL',
      },
      {
        key: 'status',
        value: 'ACTIVE',
        type: 'STRING',
        op: 'EQ',
      },
      {
        key: 'totalQty',
        value: 0,
        type: 'INT',
        op: 'GT',
      },
      {
        key: 'offerType',
        value: TYPE_OF_OFFER_AND_DEMAND.LIVE,
        type: 'STRING',
        op: 'EQ',
      },
      { ...defaultDeleteFilter },
    ],
    pageNo: 1,
    pageSize: 10,
    sortCol: 'rate',
    sortDirection: 'asc',
  });

  // Build the product name filter for GraphQL
  const productNameFilter = useMemo(() => {
    if (!selectedProductName) return [];
    return [
      {
        key: 'name',
        value: selectedProductName,
        joinAttributeName: 'grades.subCategory.product',
        type: 'STRING',
        op: 'EQ',
      },
    ];
  }, [selectedProductName]);

  // Combine all filters for the GraphQL query
  const graphqlVariables = useMemo(() => ({
    filter: [
      ...filters.filter,
      ...filterValue,
      ...productNameFilter,
    ],
    pageNo: filters.pageNo,
    pageSize: filters.pageSize,
    sortCol: filters.sortCol,
    sortDirection: filters.sortDirection,
  }), [filters, filterValue, productNameFilter]);

  const { data, loading, error, refetch } = useQuery(OFFERS_QUERY, {
    variables: graphqlVariables,
    fetchPolicy: 'network-only',
  });

  const [deleteOffer] = useMutation(DELETE_OFFER, {
    onCompleted: () => {
      toast.success(RECORD_DELETED_SUCCESSFULLY);
      setDeleteRowId('');
      setDeleteRecord(!showDeleteRecord);
      refetch();
    },
    onError: () => { },
  });

  const [updateOfferStatus] = useMutation(UPDATE_OFFER_STATUS, {
    onCompleted: () => {
      toast.success(STATUS_UPDATED_SUCCESSFULLY);
      refetch();
    },
    onError: () => { },
  });

  const toggleDeleteRecord = (id) => {
    setDeleteRecord(!showDeleteRecord);
    setDeleteRowId(id);
  };

  const openEditModal = (row) => {
    setEditRow(row);
  };

  const closeEditModal = () => {
    setEditRow(null);
  };

  const onDeleteConfirmation = async () => {
    try {
      await deleteOffer({
        variables: { id: deleteRowId },
      });
    } catch (err) {
      toast.error(err);
    }
  };

  const handleOfferStatus = async (e, cell) => {
    try {
      await updateOfferStatus({
        variables: { id: cell?.row?.original?.id, status: e.target.value },
      });
    } catch (err) {
      toast.error(err);
    }
  };

  const statusFormatter = (cell) => {
    return (
      <RBAC
        allowedRoles={[role.SUPER_ADMIN, role.ADMIN, role.SALES_HEAD]}
        disabled
      >
        {({ access }) => (
          <StatusFormatter
            options={MARKET_LIST_STATUS}
            defaultValue={cell?.row?.original?.status}
            handleChange={(e) => handleOfferStatus(e, cell)}
            inputDisabled={!access}
          />
        )}
      </RBAC>
    );
  };

  const handleLinkClick = (row) => {
    dispatch(
      setBidDetails({
        status: true,
        selectedBid: {
          ...row,
          bidType: 'offer',
          bidOfferType: 'LIVE',
          filters,
        },
      }),
    );
  };
  const linkFormatter = (cell) => {
    return (
      <div
        className="text-blue-c5 fw-medium cursor-pointer d-flex align-items-center"
        onClick={() => handleLinkClick(cell?.row?.original)}
        aria-hidden
      >
        <span className="me-2 f-16">{cell?.row?.original?.id}</span>{' '}
        <FaArrowUpRightFromSquare />
      </div>
    );
  };

  const renderEditButton = (row) => {
    return (
      <button
        type="button"
        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
        onClick={() => openEditModal(row)}
        title="Edit offer"
        aria-label="Edit offer"
      >
        <FiEdit2 />
      </button>
    );
  };

  const editFormatter = (cell) => renderEditButton(cell?.row?.original);

  const handleNameClick = (row) => {
    if (!row?.trader?.id) return;
    dispatch(
      setVendorDetails({
        status: true,
        selectedVendor: row.trader,
      }),
    );
  };

  const liveOffersColumns = offersColumns.map((column) => ({ ...column }));

  const findIndexOfItem = (item) => {
    return liveOffersColumns.findIndex((itm) => itm.accessorKey === item);
  };

  liveOffersColumns[findIndexOfItem('status')].cell = statusFormatter;
  liveOffersColumns[findIndexOfItem('id')].cell = linkFormatter;
  liveOffersColumns[findIndexOfItem('SNo')].cell = (cell) =>
    showSequenceNumber(cell, filters);
  liveOffersColumns.splice(findIndexOfItem('SNo'), 0, {
    accessorKey: 'edit',
    header: 'Edit',
    enableSorting: false,
    cell: editFormatter,
  });

  // Update the child user column to use GraphQL data
  const childUserIdx = liveOffersColumns.findIndex((itm) => itm.accessorKey === 'createdBy');
  if (childUserIdx !== -1) {
    liveOffersColumns[childUserIdx].cell = renderChildUserColumn;
    liveOffersColumns[childUserIdx].accessorFn = undefined;
  }

  liveOffersColumns.push({
    accessorKey: 'role',
    header: 'Role',
    enableSorting: false,
    cell: renderRoleColumn,
  });

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo: data?.offers?.totalCount < pageSize ? 1 : pageNo,
      pageSize,
    });
  };
  const onSortChange = (sort) => {
    setFilters({
      ...filters,
      sortCol: sort()[0].id,
      sortDirection: sort()[0].desc ? 'DESC' : 'ASC',
    });
  };

  const sortBy = [
    filters.sortCol === null
      ? { id: null, desc: null }
      : {
        id: filters.sortCol,
        desc: filters.sortDirection === 'DESC',
      },
  ];

  useEffect(() => {
    const updatedFilter = [...filters.filter];
    const updatedFirstFilter = { ...updatedFilter[0] };
    updatedFirstFilter.value = searchString;
    updatedFilter[0] = updatedFirstFilter;
    setFilters(prev => ({ ...prev, filter: updatedFilter }));
  }, [searchString]);

  const onGlobalSearch = (str) => {
    if (str) {
      setFilters((prevFilter) => {
        const updatedFilter = [...prevFilter.filter];
        const updatedFirstFilter = { ...updatedFilter[0] };
        updatedFirstFilter.value = str;
        updatedFilter[0] = updatedFirstFilter;
        return { ...prevFilter, filter: updatedFilter };
      });
    }
  };

  if (loading) return <Loader />;
  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (!data?.offers?.offers)
    return <NoDataFound message="Offers not available" />;

  return (
    <>
      {showAdvancedSearchBar && data?.offers?.offers?.length > 0 && (
        <FilterResultBar
          length={data?.offers?.offers?.length}
          type="live offers"
          handleResetFilter={handleResetFilter}
        />
      )}
      <DeleteConfirmationAlert
        open={showDeleteRecord}
        title="Delete"
        message="Do you really want to remove this record?"
        cancelButtonLabel="Close"
        addButtonLabel="Delete"
        onConfirm={onDeleteConfirmation}
        onCancel={toggleDeleteRecord}
      />
      <ReactivateListingModal
        isOpen={!!editRow}
        kind="offer"
        mode="edit"
        row={editRow}
        onClose={closeEditModal}
        onSuccess={refetch}
      />
      <DownloadCSV
        lazyQuery={EXPORTS_OFFERS}
        filters={{
          filter: graphqlVariables.filter,
          sortCol: filters.sortCol,
          sortDirection: filters.sortDirection,
        }}
        apiKey="exportOffers"
        fileName="LiveOffers"
        formats={['csv', 'excel']}
        classWrapper="market-listing-download"
        positionTop="-34px"
        positionRight="0"
      />
      <MarketListingMobileView
        rows={(data && data?.offers?.offers) || []}
        filters={filters}
        kind="offer"
        totalPages={(data && data?.offers?.totalPages) || 0}
        currentPageNo={filters.pageNo}
        currentPageSize={filters.pageSize}
        handlePageChange={handlePageChange}
        onIdClick={handleLinkClick}
        onNameClick={handleNameClick}
        renderStatusControl={(row, index) =>
          statusFormatter({ row: { original: row, index } })
        }
        renderRowAction={renderEditButton}
      >
        <FSBTable
          columns={liveOffersColumns}
          data={(data && data?.offers?.offers) || []}
          totalCount={(data && data?.offers?.totalCount) || 0}
          totalPages={(data && data?.offers?.totalPages) || 0}
          currentPageNo={filters.pageNo}
          pageSize={filters.pageSize}
          keyField="id"
          showSearch={false}
          showFilters
          showDelete={false}
          showPagination
          remote
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break"
          handlePageChange={handlePageChange}
          globalSearchStr={filters.filter[0].value}
          onGlobalSearch={onGlobalSearch}
          onSortChange={onSortChange}
          sorting={sortBy}
          searchPlaceholder="Search Vendors"
        />
      </MarketListingMobileView>
    </>
  );
};
export default LiveOffersTable;
